import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslocoService } from '@jsverse/transloco';
import { ToolbeltLocaleService } from '@mercadoni/toolbelt';
import { asapScheduler, filter, firstValueFrom, observeOn, tap } from 'rxjs';

import { DEFAULT_LOCALE, type LocaleCode, SUPPORTED_LOCALES } from '../../config/locales';
import { LOCALE_STORAGE_KEY, resolveInitialLocale } from './locale.util';
import { BUNDLED_SPANISH_GLOBALS, registerTranslator, translationVersion } from './translate-now';

/**
 * Owns the active UI language. It resolves the starting language, loads its catalogs, and keeps
 * Transloco, the document language and the Toolbelt forms in sync with it.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly transloco = inject(TranslocoService);
  private readonly toolbelt = inject(ToolbeltLocaleService);
  private readonly document = inject(DOCUMENT);
  private readonly announcer = inject(LiveAnnouncer);

  constructor() {
    registerTranslator((key, params) => this.transloco.translate(key, params));
    // Specs create many injectors: the translator of a destroyed one must not outlive it.
    inject(DestroyRef).onDestroy(() => registerTranslator(null));
    // A scope catalog can finish loading after the language changed (first visit to a feature in
    // that language): text built outside templates must be rebuilt when it does.
    this.transloco.events$
      .pipe(
        filter((event) => event.type === 'translationLoadSuccess'),
        tap((event) => {
          const { scope, langName } = event.payload;
          if (scope) this.loadedScopes.add(`${scope}/${langName}`);
        }),
        // A catalog can load while a template renders (a pipe asks for its scope), and signals
        // cannot be written during rendering.
        observeOn(asapScheduler),
        takeUntilDestroyed(),
      )
      .subscribe(() => translationVersion.update((version) => version + 1));
  }

  /** `scope/lang` of every feature catalog loaded so far; Transloco merges them into the language tree. */
  private readonly loadedScopes = new Set<string>();

  /** Identifies the latest choice; an older one that finishes late must not change anything. */
  private activation = 0;

  /** Language the UI is currently shown in. */
  readonly locale = signal<LocaleCode>(DEFAULT_LOCALE);
  /** Set when the catalogs of a chosen language could not be loaded; cleared by the next success. */
  readonly loadError = signal<string | null>(null);
  /** BCP 47 tag of the active language, to format dates and numbers with `Intl`. */
  readonly dateLocale = computed(
    () =>
      SUPPORTED_LOCALES.find((entry) => entry.code === this.locale())?.dateLocale ?? DEFAULT_LOCALE,
  );

  /**
   * Resolves and applies the starting language. Run before the shell renders so no untranslated
   * key is ever painted; falls back to Spanish if the catalogs cannot be loaded.
   */
  async init(): Promise<void> {
    await this.activate(resolveInitialLocale(this.readStored(), navigator.languages), false);
  }

  /**
   * Switches the UI language and remembers the choice. The previous language stays visible until
   * every catalog of the new one is ready; on failure Spanish is shown and the choice is not saved.
   */
  async setLocale(code: LocaleCode): Promise<void> {
    await this.activate(code, true);
  }

  private async activate(code: LocaleCode, persist: boolean): Promise<void> {
    const activation = ++this.activation;
    try {
      await Promise.all([
        ...[...new Set([code, DEFAULT_LOCALE])].map((lang) => firstValueFrom(this.transloco.load(lang))),
        ...this.loadVisitedScopes(code),
      ]);
      if (activation !== this.activation) return;
      this.loadError.set(null);
      this.apply(code);
      if (persist) {
        this.store(code);
        this.announceChange(code);
      }
    } catch (error) {
      if (activation !== this.activation) return;
      this.loadError.set(error instanceof Error ? error.message : String(error));
      // The Spanish globals may be the ones that failed: install the bundled copy so the shell
      // never renders with an empty translation tree.
      this.transloco.setTranslation(BUNDLED_SPANISH_GLOBALS, DEFAULT_LOCALE);
      this.apply(DEFAULT_LOCALE);
    }
  }

  /**
   * Starts loading, in the new language, every feature catalog already loaded in the current one, so
   * the screen on display changes together with the shell. A catalog that fails is ignored: that
   * screen keeps showing Spanish, which is the fallback.
   */
  private loadVisitedScopes(code: LocaleCode): Promise<unknown>[] {
    const scopes = new Set([...this.loadedScopes].map((path) => path.slice(0, path.lastIndexOf('/'))));
    return [...scopes]
      .filter((scope) => !this.loadedScopes.has(`${scope}/${code}`))
      .map((scope) => firstValueFrom(this.transloco.load(`${scope}/${code}`)).catch(() => undefined));
  }

  private apply(code: LocaleCode): void {
    this.transloco.setActiveLang(code);
    this.locale.set(code);
    translationVersion.update((version) => version + 1);
    this.document.documentElement.lang = code;
    this.toolbelt.setLocale(code);
  }

  /** Tells assistive technology the language changed, in the new language. */
  private announceChange(code: LocaleCode): void {
    const language = SUPPORTED_LOCALES.find((entry) => entry.code === code)?.label ?? code;
    void this.announcer.announce(this.transloco.translate('layout.language.changed', { language }), 'polite');
  }

  private readStored(): string | null {
    try {
      return localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private store(code: LocaleCode): void {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, code);
    } catch {
      // Storage blocked: the language still applies for this session, it is just not remembered.
    }
  }
}
